import React from "react";
import moment from "moment/moment";
import { Avatar, Tooltip } from "@fluentui/react-components";
import { Eye, Download } from "lucide-react";
import HoverCard from "../hoverCard/HoverCard";
import { utcToLocalDateTimeString } from "../../utils/systemSettings";
import styles from "./WsrReportsView.module.scss";

const formatPeriod = (period) => {
  if (!period) return { range: period, month: "", day: "" };
  const parsed = moment(period.toString(), "YYMMDD");
  if (!parsed.isValid()) return { range: period, month: "", day: "" };
  const start = parsed.clone().subtract(4, "days");
  return {
    range: `${start.format("MMM D")} - ${parsed.format("MMM D, YYYY")} (${period})`,
    month: parsed.format("MMM"),
    day: parsed.format("D"),
  };
};

const CardsSkeleton = ({ count = 4 }) => (
  <div className={styles.timeline}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={styles.timelineRow}>
        <div className={styles.timelineMarker}>
          <div className={`${styles.shimmer} ${styles.timelineDot}`} />
        </div>
        <div className={styles.timelineCard}>
          <div className={`${styles.shimmer} ${styles.skeletonCardTitle}`} />
          <div className={`${styles.shimmer} ${styles.skeletonCardLine}`} />
        </div>
      </div>
    ))}
  </div>
);

const ActionIcon = ({ tooltip, onClick, children }) => (
  <Tooltip content={tooltip} relationship="label">
    <button type="button" className={styles.cardActionIcon} onClick={onClick}>
      {children}
    </button>
  </Tooltip>
);

const WsrReportsCardsView = ({ reports, loading, settings, onPreview, onDownload }) => {
  if (loading && (!reports || reports.length === 0)) {
    return <CardsSkeleton />;
  }

  if (!reports || reports.length === 0) {
    return <div className={styles.emptyState}>No locked reports yet.</div>;
  }

  return (
    <div className={styles.timeline}>
      {reports.map((report) => {
        const period = formatPeriod(report.period);
        return (
          <div key={report.period} className={styles.timelineRow}>
            <div className={styles.timelineMarker}>
              <div className={styles.timelineDateBadge}>
                <span className={styles.timelineDateMonth}>{period.month}</span>
                <span className={styles.timelineDateDay}>{period.day}</span>
              </div>
              <div className={styles.timelineDot} />
            </div>
            <div className={`${styles.timelineCard} ${styles.timelineCardInline}`}>
              <span className={`${styles.timelineCardTitle} ${styles.timelineColPeriod}`}>{period.range}</span>

              <div className={styles.timelineColConfirmedBy}>
                {report.confirmedBy && (
                  <HoverCard
                    CanSendMessage={false}
                    HoverCardTrigger={
                      <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                        <Avatar
                          size={20}
                          color="colorful"
                          name={report.confirmedBy.name}
                          image={{ src: report.confirmedBy.image }}
                        />
                        <span className="ms-2 text-truncate">{report.confirmedBy.name}</span>
                      </div>
                    }
                    AvatarId={report.confirmedBy.id}
                    AvatarName={report.confirmedBy.name}
                    AvatarImage={report.confirmedBy.image}
                    AvatarDesignation={report.confirmedBy.title}
                    AvatarEmail={report.confirmedBy.email}
                    AvatarPhone={report.confirmedBy.phoneNumber}
                    AvatarIsExternal={report.confirmedBy.isExternal}
                  />
                )}
              </div>

              <span className={`${styles.timelineCardMeta} ${styles.timelineColConfirmedAt}`}>
                {report.confirmedAt ? utcToLocalDateTimeString(settings, report.confirmedAt) : "-"}
              </span>

              <div className={styles.cardActions}>
                <ActionIcon tooltip="Preview" onClick={() => onPreview(report)}>
                  <Eye size={15} />
                </ActionIcon>
                <ActionIcon tooltip="Download" onClick={() => onDownload(report)}>
                  <Download size={15} />
                </ActionIcon>
              </div>
            </div>
          </div>
        );
      })}
      {loading && <div className={styles.loadMoreSpinner} />}
    </div>
  );
};

export default WsrReportsCardsView;
